import { MessageBar, MessageBarBody } from '@fluentui/react-components';
import { findStepType } from '@vigeo/shared';
import type { StepPortalProps, StepReviewProps } from './contract.js';
import { findStepUi } from './registry.js';

/**
 * Render a step without knowing which one it is.
 *
 * Both renderers dispatch on the id through the two registries and never name a concrete
 * type. That is what keeps the portal and the review queue from growing a switch statement
 * that has to be edited every time a step type is added — the failure CLAUDE.md rule 6
 * exists to prevent.
 *
 * An unknown id is a data problem, not a programming one: a stored instance can name a type
 * that was removed from the build. It renders a plain message rather than throwing, because
 * one retired step type must not take down a person's whole checklist.
 */

export interface RenderStepProps {
  readonly type: string;
}

export function PortalStepRenderer({ type, ...props }: RenderStepProps & StepPortalProps) {
  const kernel = findStepType(type);
  const ui = findStepUi(type);

  if (kernel === undefined || ui === undefined) {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>This item cannot be shown right now. HR has been notified.</MessageBarBody>
      </MessageBar>
    );
  }

  // A type with no portal half is HR-recorded by design (ADR-005): nothing to show here.
  if (ui.portal === null) {
    return (
      <MessageBar intent="info">
        <MessageBarBody>{kernel.label} is recorded by our office. There is nothing for you to do.</MessageBarBody>
      </MessageBar>
    );
  }

  const Portal = ui.portal;
  /**
   * READ-ONLY IS FOLDED IN HERE, not in eight components.
   *
   * Every portal already honours `disabled` on its inputs, and each of them is one edit away from
   * a reviewer forgetting. Folding it once means the guarantee holds for a step type nobody has
   * written yet, and `tests/portal-read-only.test.tsx` renders EVERY registered type to prove it
   * rather than trusting this sentence.
   */
  return <Portal {...props} disabled={props.disabled || props.readOnly === true} />;
}

export function ReviewStepRenderer({ type, ...props }: RenderStepProps & StepReviewProps) {
  const ui = findStepUi(type);

  if (ui === undefined) {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          No reviewer for step type “{type}”. It cannot be approved until that is fixed.
        </MessageBarBody>
      </MessageBar>
    );
  }

  const Review = ui.review;
  return <Review {...props} />;
}
